import { useState } from 'react';
import { z } from 'zod';

import { Alert, Button, Card, setSubmitError, Skeleton, useAppForm } from '@keboola/design';

import { useAuth } from '../../../auth/context';
import { getApiErrorMessage } from '../../auth/errors';
import { formValidators } from '../../auth/validate';
import { toVendorOptions } from '../gate';
import { useAllVendors, useCreateVendor, useRequestJoinVendor } from '../queries';

/**
 * Blocks the vendor dashboard until the user belongs to an approved vendor.
 *
 * - States: join an existing vendor, create one, or wait for an admin after either.
 * - A new vendor's temporary `_v`-prefixed id stays unselectable until an admin approves and renames it.
 */
export function SelectVendorGate() {
  const [phase, setPhase] = useState<'select' | 'create' | 'requested'>('select');

  if (phase === 'requested') return <VendorRequested />;
  if (phase === 'create') {
    return (
      <CreateVendorForm
        onRequested={() => setPhase('requested')}
        onCancel={() => setPhase('select')}
      />
    );
  }
  return (
    <SelectVendorForm
      onRequested={() => setPhase('requested')}
      onCreate={() => setPhase('create')}
    />
  );
}

function VendorRequested() {
  const { signOut } = useAuth();

  return (
    <Card className="tw:mx-auto tw:max-w-lg">
      <Card.Content className="tw:flex tw:flex-col tw:gap-4 tw:text-center">
        <Card.Title as="h1">Request received</Card.Title>
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          A Keboola administrator has to confirm your vendor before you can manage components. We
          will email you when that happens.
        </p>
        <Button variant="secondary" onClick={() => void signOut()}>
          Sign out
        </Button>
      </Card.Content>
    </Card>
  );
}

const selectVendorSchema = z.object({ vendorId: z.string().min(1, 'Pick a vendor') });

function SelectVendorForm({
  onRequested,
  onCreate,
}: {
  onRequested: () => void;
  onCreate: () => void;
}) {
  const { data: vendors, isLoading } = useAllVendors();
  const requestJoin = useRequestJoinVendor();

  const form = useAppForm({
    defaultValues: { vendorId: '' },
    validators: formValidators(selectVendorSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        await requestJoin.mutateAsync(value.vendorId);
        onRequested();
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'Could not send the request.'));
      }
    },
  });

  const options = toVendorOptions(vendors ?? []);

  return (
    <Card className="tw:mx-auto tw:max-w-lg">
      <Card.Content className="tw:flex tw:flex-col tw:gap-4">
        <Card.Title as="h1">Join a vendor</Card.Title>
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          Components belong to a vendor. Ask to join the one you work for, or register a new one.
        </p>

        {isLoading ? (
          <Skeleton className="tw:h-10" />
        ) : (
          <form.AppForm>
            <form.FormBody>
              <form.AppField name="vendorId">
                {(field) => (
                  <field.SelectField
                    label="Vendor"
                    required
                    items={options}
                    placeholder="Select a vendor"
                  />
                )}
              </form.AppField>
              <form.SubmitErrorAlert />
              <form.SubmitButton>Request access</form.SubmitButton>
            </form.FormBody>
          </form.AppForm>
        )}

        <Button variant="invisible" onClick={onCreate}>
          My vendor is not listed — register a new one
        </Button>
      </Card.Content>
    </Card>
  );
}

const createVendorSchema = z.object({
  name: z.string().min(1, 'Required').max(64),
  email: z.string().min(1, 'Required').pipe(z.email('Enter a valid email address')),
  address: z.string().min(1, 'Required'),
});

function CreateVendorForm({
  onRequested,
  onCancel,
}: {
  onRequested: () => void;
  onCancel: () => void;
}) {
  const createVendor = useCreateVendor();

  const form = useAppForm({
    defaultValues: { name: '', email: '', address: '' },
    validators: formValidators(createVendorSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        await createVendor.mutateAsync(value);
        onRequested();
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'Could not register the vendor.'));
      }
    },
  });

  return (
    <Card className="tw:mx-auto tw:max-w-lg">
      <Card.Content className="tw:flex tw:flex-col tw:gap-4">
        <Card.Title as="h1">Register a vendor</Card.Title>
        <Alert variant="info">
          A Keboola administrator reviews new vendors before they can publish components.
        </Alert>

        <form.AppForm>
          <form.FormBody>
            <form.AppField name="name">
              {(field) => <field.TextField label="Vendor name" required placeholder="Acme Co." />}
            </form.AppField>
            <form.AppField name="email">
              {(field) => (
                <field.TextField
                  label="Contact email"
                  type="email"
                  required
                  placeholder="dev@acme.example"
                  help="Where Keboola will contact you about your components."
                />
              )}
            </form.AppField>
            <form.AppField name="address">
              {(field) => <field.TextField label="Address" required />}
            </form.AppField>
            <form.SubmitErrorAlert />
            <form.SubmitButton>Register vendor</form.SubmitButton>
          </form.FormBody>
        </form.AppForm>

        <Button variant="invisible" onClick={onCancel}>
          Back to the vendor list
        </Button>
      </Card.Content>
    </Card>
  );
}
